<template>
  <modal-v2 :show="show" :on-close="closeModal">
    <div class="h-auto overflow-auto flex flex-col">
      <woot-modal-header-v2
        :header-title="header"
        :header-content="
          $t('INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.FORM.MODAL_DESC')
        "
      />

      <form class="flex flex-col w-full" @submit.prevent="submit">
        <div
          class="px-6 py-5 flex flex-col gap-4 max-h-[calc(100vh-250px)] overflow-y-auto"
        >
          <input-v2
            v-model.trim="app.title"
            data-testid="app-title"
            :label="
              $t('INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.FORM.TITLE_LABEL')
            "
            :placeholder="
              $t(
                'INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.FORM.TITLE_PLACEHOLDER'
              )
            "
            :error="titleError"
            @input="$v.app.title.$touch"
          />

          <input-v2
            v-model.trim="app.content.url"
            data-testid="app-url"
            :dir="app.content.url ? 'ltr' : null"
            input-class="rtl:text-right"
            :label="
              $t('INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.FORM.URL_LABEL')
            "
            :placeholder="
              $t('INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.FORM.URL_PLACEHOLDER')
            "
            :hint="$t('INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.FORM.URL_HINT')"
            :error="urlError"
            @input="$v.app.content.url.$touch"
          />

          <select-v2
            v-model="app.content.type"
            :options="typeOptions"
            :label="
              $t('INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.FORM.TYPE_LABEL')
            "
          />

          <select-v2
            v-if="app.content.type === 'link'"
            v-model="app.content.link_attribute"
            :options="attributeOptions"
            :label="
              $t(
                'INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.FORM.LINK_ATTRIBUTE_LABEL'
              )
            "
            :placeholder="
              $t(
                'INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.FORM.LINK_ATTRIBUTE_PLACEHOLDER'
              )
            "
          />
        </div>

        <modal-footer-v2
          :cancel-text="
            $t('INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.CREATE.FORM_CANCEL')
          "
          :confirm-text="submitButtonLabel"
          confirm-type="submit"
          :is-disabled="$v.$invalid"
          :is-loading="isLoading"
          @cancel="closeModal"
        />
      </form>
    </div>
  </modal-v2>
</template>

<script>
import { required, url } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import attributeMixin from 'dashboard/mixins/attributeMixin';

export default {
  mixins: [alertMixin, attributeMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'CREATE',
    },
    selectedAppData: {
      type: Object,
      default: () => ({}),
    },
  },
  validations: {
    app: {
      title: { required },
      content: {
        type: { required },
        url: { required, url },
      },
    },
  },
  data() {
    return {
      isLoading: false,
      app: {
        title: '',
        content: {
          type: 'frame',
          url: '',
          link_attribute: null,
        },
      },
    };
  },
  computed: {
    header() {
      return this.$t(
        `INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.${this.mode}.HEADER`
      );
    },
    submitButtonLabel() {
      return this.$t(
        `INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.${this.mode}.FORM_SUBMIT`
      );
    },
    typeOptions() {
      const types = this.$t(
        'INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.FORM.TYPES'
      );
      if (!types || typeof types !== 'object') return [];
      return Object.entries(types).map(([type, value]) => ({
        value: type,
        label: value.TEXT,
      }));
    },
    attributeOptions() {
      return this.attributesToSelect.map(attribute => ({
        key: attribute.attribute_key,
        value: attribute.object,
        label: attribute.attribute_display_name,
      }));
    },
    attributesToSelect() {
      return this.$store.getters['attributes/getAttributes']
        .filter(x => x.attribute_model === 'conversation_attribute')
        .map(x => ({
          ...x,
          object: { key: x.attribute_key, model: x.attribute_model },
        }));
    },
    titleError() {
      return this.$v.app.title.$error
        ? this.$t('INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.FORM.TITLE_ERROR')
        : '';
    },
    urlError() {
      return this.$v.app.content.url.$error
        ? this.$t('INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.FORM.URL_ERROR')
        : '';
    },
  },
  mounted() {
    if (this.mode === 'UPDATE' && this.selectedAppData) {
      this.app.title = this.selectedAppData.title;
      this.app.content = {
        link_attribute: null,
        ...this.selectedAppData.content[0],
      };
    }
  },
  methods: {
    closeModal() {
      // Reset the data once closed
      this.app = {
        title: '',
        content: { type: 'frame', url: '', link_attribute: null },
      };
      this.$emit('close');
    },
    async submit() {
      try {
        this.$v.$touch();
        if (this.$v.$invalid) {
          return;
        }
        const action = this.mode.toLowerCase();
        const content = {
          type: this.app.content.type,
          url: this.app.content.url,
        };
        if (content.type === 'link' && this.app.content.link_attribute) {
          content.link_attribute = this.app.content.link_attribute;
        }
        const payload = {
          title: this.app.title,
          content: [content],
        };

        if (action === 'update') {
          payload.id = this.selectedAppData.id;
        }

        this.isLoading = true;
        await this.$store.dispatch(`dashboardApps/${action}`, payload);
        this.showAlert(
          this.$t(
            `INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.${this.mode}.API_SUCCESS`
          )
        );
        this.closeModal();
      } catch (err) {
        this.showAlert(
          this.$t(
            `INTEGRATION_SETTINGS.EXTERNAL_DASHBOARD.${this.mode}.API_ERROR`
          )
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
